/**
 * src/features/learning/hooks/useLearningData.ts
 *
 * MER Algo Footprints v7.1H — Learning System API Hook (v1 Hardened)
 *
 * Types updated to match hardened server schemas:
 *   - AIReview: enum values updated to strict v1 spec
 *   - AdaptiveEdgeSummary: removed recommendations, calibration, false_positive_patterns,
 *     confidence_adjustments; added setup_stats, regime_stats, symbol_stats
 */

import { useState, useCallback, useRef } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LearningSnapshot {
  snapshot_id:     string;
  symbol:          string;
  timestamp:       number;
  bias:            string | null;
  setup_type:      string | null;
  confidence:      number | null;
  readiness_score: number | null;
  regime_label:    string | null;
  smc_state:       string | null;
  liquidity_summary: string | null;
  gamma_state:     string | null;
  trap_state:      string | null;
  trigger:         string | null;
  invalidation:    string | null;
  targets:         { t1?: string; t2?: string } | null;
  thesis_summary:  string | null;
  action_plan:     string | null;
  user_action:     'entered' | 'passed' | 'watching' | null;
  outcome_eval:    OutcomeEval | null;
  ai_review:       AIReview | null;
}

export interface JournalInputs {
  user_action:   'entered' | 'passed' | 'watching' | null;
  pass_reason:   string;
  followed_plan: 'yes' | 'partial' | 'no' | null;
}

export interface OutcomeEval {
  classification:  string | null;
  is_complete:     boolean;
  windows:         Record<string, WindowEval>;
  last_evaluated:  number;
}

export interface WindowEval {
  evaluated_at:    number;
  price_at_window: number;
  mfe:             number | null;
  mae:             number | null;
  target_hit:      { t1: boolean; t2: boolean };
  invalidated:     boolean;
  close_vs_entry:  number | null;
  structure_followed: boolean;
  classification?: string;
}

// Strict v1 enums — must match aiReviewEngine.js
export interface AIReview {
  thesis_quality:      'correct' | 'early' | 'late' | 'wrong';
  setup_quality:       'high' | 'medium' | 'low';
  execution_alignment: 'aligned' | 'partial' | 'deviated';
  confidence_accuracy: 'overestimated' | 'accurate' | 'underestimated';
  primary_driver:      'structure' | 'liquidity' | 'trap' | 'gamma';
  failure_driver:      'none' | 'timing' | 'liquidity' | 'structure_break';
  lesson:              string | null;
  journal_draft:       string | null;
  generated_at:        number;
}

// v1 basic stats only
export interface GroupStats {
  total:    number;
  wins:     number;
  losses:   number;
  win_rate: number | null;
}

export interface AdaptiveEdgeSummary {
  computed_at:      number;
  total_analyzed:   number;
  total_completed:  number;
  overall:          GroupStats;
  setup_stats:      Record<string, GroupStats>;
  regime_stats:     Record<string, GroupStats>;
  symbol_stats:     Record<string, GroupStats>;
  note?:            string;
}

// ── API base ──────────────────────────────────────────────────────────────────

function apiBase(): string {
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return 'http://localhost:3000';
}

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as any).error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface UseLearningDataReturn {
  snapshots:        LearningSnapshot[];
  snapshotsLoading: boolean;
  snapshotsError:   string | null;
  loadSnapshots:    (symbol: string, limit?: number) => Promise<void>;
  loadSnapshot:     (id: string) => Promise<LearningSnapshot | null>;
  reviewLoading:    boolean;
  reviewError:      string | null;
  triggerReview:    (snapshotId: string) => Promise<AIReview | null>;
  recordUserAction: (snapshotId: string, action: 'entered' | 'passed' | 'watching' | null) => Promise<boolean>;
  journalAccept:    (snapshotId: string, inputs: JournalInputs, draftText: string) => Promise<boolean>;
  journalDiscard:   (snapshotId: string) => Promise<boolean>;
  edgeSummary:      AdaptiveEdgeSummary | null;
  edgeLoading:      boolean;
  edgeError:        string | null;
  loadEdge:         (forceRefresh?: boolean) => Promise<void>;
}

export function useLearningData(): UseLearningDataReturn {
  const [snapshots,        setSnapshots]        = useState<LearningSnapshot[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [snapshotsError,   setSnapshotsError]   = useState<string | null>(null);
  const [reviewLoading,    setReviewLoading]    = useState(false);
  const [reviewError,      setReviewError]      = useState<string | null>(null);
  const [edgeSummary,      setEdgeSummary]      = useState<AdaptiveEdgeSummary | null>(null);
  const [edgeLoading,      setEdgeLoading]      = useState(false);
  const [edgeError,        setEdgeError]        = useState<string | null>(null);
  const loadingRef = useRef<string | null>(null);

  const loadSnapshots = useCallback(async (symbol: string, limit = 50) => {
    const key = `${symbol}:${limit}`;
    if (loadingRef.current === key) return;
    loadingRef.current = key;
    setSnapshotsLoading(true);
    setSnapshotsError(null);
    try {
      const data = await apiFetch<{ ok: boolean; snapshots: LearningSnapshot[] }>(
        `/api/learning/snapshots/${encodeURIComponent(symbol)}?limit=${limit}`
      );
      setSnapshots(data.snapshots || []);
    } catch (err: any) {
      setSnapshotsError(err.message);
    } finally {
      setSnapshotsLoading(false);
      loadingRef.current = null;
    }
  }, []);

  const loadSnapshot = useCallback(async (id: string): Promise<LearningSnapshot | null> => {
    try {
      const data = await apiFetch<{ ok: boolean; snapshot: LearningSnapshot }>(
        `/api/learning/snapshot/${encodeURIComponent(id)}`
      );
      return data.snapshot || null;
    } catch {
      return null;
    }
  }, []);

  const triggerReview = useCallback(async (snapshotId: string): Promise<AIReview | null> => {
    setReviewLoading(true);
    setReviewError(null);
    try {
      const data = await apiFetch<{ ok: boolean; review: AIReview }>(
        `/api/learning/review/${encodeURIComponent(snapshotId)}`,
        { method: 'POST' }
      );
      setSnapshots(prev =>
        prev.map(s => s.snapshot_id === snapshotId ? { ...s, ai_review: data.review } : s)
      );
      return data.review || null;
    } catch (err: any) {
      setReviewError(err.message);
      return null;
    } finally {
      setReviewLoading(false);
    }
  }, []);

  const recordUserAction = useCallback(async (
    snapshotId: string,
    action: 'entered' | 'passed' | 'watching' | null
  ): Promise<boolean> => {
    try {
      await apiFetch(`/api/learning/user-action/${encodeURIComponent(snapshotId)}`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      setSnapshots(prev =>
        prev.map(s => s.snapshot_id === snapshotId ? { ...s, user_action: action } : s)
      );
      return true;
    } catch {
      return false;
    }
  }, []);

  const loadEdge = useCallback(async (forceRefresh = false) => {
    setEdgeLoading(true);
    setEdgeError(null);
    try {
      const path = forceRefresh ? '/api/learning/edge/refresh' : '/api/learning/edge';
      const data = await apiFetch<{ ok: boolean; summary: AdaptiveEdgeSummary }>(path);
      setEdgeSummary(data.summary || null);
    } catch (err: any) {
      setEdgeError(err.message);
    } finally {
      setEdgeLoading(false);
    }
  }, []);

  const journalAccept = useCallback(async (
    snapshotId: string,
    inputs: JournalInputs,
    draftText: string
  ): Promise<boolean> => {
    try {
      await apiFetch(`/api/learning/journal/${encodeURIComponent(snapshotId)}/accept`, {
        method: 'POST',
        body: JSON.stringify({ inputs, journal_draft: draftText }),
      });
      setSnapshots(prev =>
        prev.map(s => s.snapshot_id === snapshotId
          ? { ...s, user_action: inputs.user_action, journal_status: 'accepted' }
          : s
        )
      );
      return true;
    } catch { return false; }
  }, []);

  const journalDiscard = useCallback(async (snapshotId: string): Promise<boolean> => {
    try {
      await apiFetch(`/api/learning/journal/${encodeURIComponent(snapshotId)}/discard`, {
        method: 'POST',
      });
      setSnapshots(prev =>
        prev.map(s => s.snapshot_id === snapshotId
          ? { ...s, journal_status: 'discarded' }
          : s
        )
      );
      return true;
    } catch { return false; }
  }, []);

  return {
    snapshots, snapshotsLoading, snapshotsError, loadSnapshots,
    loadSnapshot,
    reviewLoading, reviewError, triggerReview,
    recordUserAction,
    journalAccept, journalDiscard,
    edgeSummary, edgeLoading, edgeError, loadEdge,
  };
}
